import React, { useState } from 'react';
import Select from 'react-select';
import { styleSelect, themeSelect } from '../../../../../rbts/assets/ts/_utils';
import {
  formatPrice,
  TableList,
  maskPhoneNumber,
  cleanPhoneNumber,
  SimpleAlert,
} from '../../../../../rbts/helpers';
import { useReporteCA, SearchTypeCA } from '../hooks/ReporteCAHook';
import { useObtenerDiasPorEvento } from '../../../reportes/cortesias/hooks/CortesiasHook';
import * as ExcelJS from 'exceljs';
import { ReporteCARow } from '../models/ReporteCAModel';
import {
  getListadoFolios,
  getHistorialRecargas,
  getMovimientos,
} from '../../../../service/enlistment';

const SEARCH_LABELS: Record<SearchTypeCA, string> = {
  folio: 'Folio',
  telefono: 'Teléfono',
  nombre: 'Nombre',
};

const SEARCH_PLACEHOLDERS: Record<SearchTypeCA, string> = {
  folio: 'Ingrese folio ticketcash',
  telefono: 'Ingrese teléfono (10 dígitos)',
  nombre: 'Ingrese nombre del titular',
};

const buildRowsLocal = (recargas: any[], movimientos: any[], folio: string): ReporteCARow[] => {
  const cargaRows: ReporteCARow[] = (Array.isArray(recargas) ? recargas : []).map((r: any) => ({
    folio: r.folio || folio,
    dia_label: r.dia_administrativo || '',
    id_dia: null,
    tipo: 'CARGA',
    descripcion: r.tipo_recarga || '',
    monto: Math.abs(Number(r.recarga) || 0),
    cantidad: null,
  }));
  const abonoRows: ReporteCARow[] = (Array.isArray(movimientos) ? movimientos : []).map((m: any) => ({
    folio: m.folio || folio,
    dia_label: m.fecha || '',
    id_dia: m.id_dia || null,
    tipo: 'ABONO',
    descripcion: [m.punto_venta_nombre, m.producto_nombre].filter(Boolean).join(' — '),
    monto: Math.abs(Number(m.costo_total) || 0),
    cantidad: Number(m.cantidad) || null,
  }));
  return [...cargaRows, ...abonoRows];
};

const View: React.FC = () => {
  const [searchType, setSearchType] = useState<SearchTypeCA>('folio');
  const [searchValue, setSearchValue] = useState('');
  const [phoneClean, setPhoneClean] = useState('');
  const [diasSeleccionados, setDiasSeleccionados] = useState<any[]>([]);
  const [mostrarTotales, setMostrarTotales] = useState(false);

  // Estado para exportación masiva
  const [todosFoliosEnabled, setTodosFoliosEnabled] = useState(false);
  const [listadoFolios, setListadoFolios] = useState<{ folio: string; esWallet: boolean }[]>([]);
  const [loadingListado, setLoadingListado] = useState(false);
  const [incluirWallet, setIncluirWallet] = useState(false);
  const [folioInicio, setFolioInicio] = useState<string | null>(null);
  const [folioFin, setFolioFin] = useState<string | null>(null);
  const [exportandoTodos, setExportandoTodos] = useState(false);
  const [mostrarTotalesExport, setMostrarTotalesExport] = useState(false);

  const { rows, clienteInfoList, loading, search } = useReporteCA();
  const { list: diasList } = useObtenerDiasPorEvento(0);

  const filteredRows: ReporteCARow[] =
    diasSeleccionados.length > 0
      ? rows.filter((r) =>
          diasSeleccionados.some((d: any) => d.label === r.dia_label)
        )
      : rows;

  const totalCargas = filteredRows
    .filter((r) => r.tipo === 'CARGA')
    .reduce((s, r) => s + r.monto, 0);

  const totalAbonos = filteredRows
    .filter((r) => r.tipo === 'ABONO')
    .reduce((s, r) => s + r.monto, 0);

  // Lista filtrada según el toggle de Wallet
  const listadoFiltrado = incluirWallet
    ? listadoFolios
    : listadoFolios.filter((f) => !f.esWallet);

  // Folios en el rango seleccionado
  const idxInicio = folioInicio ? listadoFiltrado.findIndex((f) => f.folio === folioInicio) : -1;
  const idxFin = folioFin ? listadoFiltrado.findIndex((f) => f.folio === folioFin) : -1;
  const foliosEnRango =
    idxInicio >= 0 && idxFin >= 0 && idxInicio <= idxFin
      ? listadoFiltrado.slice(idxInicio, idxFin + 1)
      : [];

  const toOption = (f: { folio: string; esWallet: boolean }) => ({
    value: f.folio,
    label: f.folio,
  });
  const folioOptions = listadoFiltrado.map(toOption);
  const folioFinOptions =
    idxInicio >= 0
      ? listadoFiltrado.slice(idxInicio).map(toOption)
      : folioOptions;

  const handleToggle = (type: SearchTypeCA) => {
    if (type === searchType) return;
    setSearchType(type);
    setSearchValue('');
    setPhoneClean('');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const maskedValue = maskPhoneNumber(raw);
    setSearchValue(maskedValue);
    setPhoneClean(cleanPhoneNumber(maskedValue));
  };

  const handleBuscar = () => {
    const value =
      searchType === 'telefono' ? phoneClean : searchValue.trim();
    if (!value) {
      SimpleAlert('Ingresa un valor para buscar', 3000, 'error');
      return;
    }
    search(searchType, value);
  };

  const handleTodosFoliosToggle = async (enabled: boolean) => {
    setTodosFoliosEnabled(enabled);
    if (!enabled) {
      setFolioInicio(null);
      setFolioFin(null);
      return;
    }
    if (listadoFolios.length === 0) {
      setLoadingListado(true);
      try {
        const folios = await getListadoFolios();
        setListadoFolios(Array.isArray(folios) ? folios : []);
        setFolioInicio(null);
        setFolioFin(null);
      } catch {
        SimpleAlert('Error al cargar el listado de folios', 3000, 'error');
        setTodosFoliosEnabled(false);
      } finally {
        setLoadingListado(false);
      }
    }
  };

  const handleExportTodos = async () => {
    if (foliosEnRango.length === 0) return;
    setExportandoTodos(true);
    try {
      const results = await Promise.all(
        foliosEnRango.map(async ({ folio, esWallet }) => {
          const [recargas, movimientos] = await Promise.all([
            getHistorialRecargas(folio).catch(() => []),
            getMovimientos(folio).catch(() => []),
          ]);
          return { folio, esWallet, rows: buildRowsLocal(recargas, movimientos, folio) };
        })
      );

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Todos los Folios');
      sheet.properties.defaultRowHeight = 18;
      sheet.columns = [
        { key: 'a', width: 18 },
        { key: 'b', width: 10 },
        { key: 'c', width: 45 },
        { key: 'd', width: 15 },
        { key: 'e', width: 10 },
      ];

      // Título
      sheet.mergeCells('A1:E1');
      sheet.getCell('A1').value = 'Reporte de Cargas y Abonos — Exportación masiva';
      sheet.getRow(1).height = 30;
      sheet.getRow(1).font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF8C24' } };

      // Subtítulo
      sheet.mergeCells('A2:E2');
      sheet.getCell('A2').value = `Folios ${folioInicio} — ${folioFin}  (${foliosEnRango.length} folios)`;
      sheet.getRow(2).font = { name: 'Arial', size: 10, italic: true, color: { argb: '888888' } };

      let rowIdx = 3;

      for (const { folio, esWallet, rows: folioRows } of results) {
        rowIdx++; // separador en blanco

        // Encabezado de sección por folio
        sheet.mergeCells(`A${rowIdx}:E${rowIdx}`);
        sheet.getCell(`A${rowIdx}`).value = `Folio: ${folio}`;
        sheet.getRow(rowIdx).height = 22;
        sheet.getRow(rowIdx).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF8C24' },
        };
        sheet.getRow(rowIdx).font = {
          name: 'Arial',
          size: 12,
          bold: true,
          color: { argb: 'FFFFFFFF' },
        };
        rowIdx++;

        if (folioRows.length === 0) {
          sheet.getCell(`A${rowIdx}`).value = 'Sin movimientos registrados';
          sheet.getCell(`A${rowIdx}`).font = { italic: true, color: { argb: '999999' } };
          rowIdx++;
          continue;
        }

        // Encabezados de columna
        sheet.getCell(`A${rowIdx}`).value = 'Día Administrativo';
        sheet.getCell(`B${rowIdx}`).value = 'Tipo';
        sheet.getCell(`C${rowIdx}`).value = 'Descripción';
        sheet.getCell(`D${rowIdx}`).value = 'Monto';
        sheet.getCell(`E${rowIdx}`).value = 'Cantidad';
        sheet.getRow(rowIdx).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1A1A1A' },
        };
        sheet.getRow(rowIdx).font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        rowIdx++;

        // Filas de datos
        let totalCargasFolio = 0;
        let totalAbonosFolio = 0;
        for (const r of folioRows) {
          sheet.getCell(`A${rowIdx}`).value = r.dia_label;
          sheet.getCell(`B${rowIdx}`).value = r.tipo;
          sheet.getCell(`C${rowIdx}`).value = r.descripcion;
          sheet.getCell(`D${rowIdx}`).value = r.tipo === 'CARGA' ? r.monto : -r.monto;
          sheet.getCell(`E${rowIdx}`).value = r.cantidad ?? '';
          if (r.tipo === 'CARGA') totalCargasFolio += r.monto;
          else totalAbonosFolio += r.monto;
          rowIdx++;
        }

        // Totales del folio
        if (mostrarTotalesExport) {
          sheet.getCell(`C${rowIdx}`).value = 'Total Cargas';
          sheet.getCell(`D${rowIdx}`).value = totalCargasFolio;
          sheet.getRow(rowIdx).font = { bold: true };
          rowIdx++;
          sheet.getCell(`C${rowIdx}`).value = 'Total Abonos';
          sheet.getCell(`D${rowIdx}`).value = -totalAbonosFolio;
          sheet.getRow(rowIdx).font = { bold: true };
          rowIdx++;
          sheet.getCell(`C${rowIdx}`).value = 'Saldo resultante';
          sheet.getCell(`D${rowIdx}`).value = totalCargasFolio - totalAbonosFolio;
          sheet.getRow(rowIdx).font = { bold: true };
          rowIdx++;
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(blob);
      anchor.download = `reporte_folios_${folioInicio}_${folioFin}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(anchor.href);
    } catch (err: any) {
      SimpleAlert(
        err?.response?.data?.message || 'Error al generar el Excel',
        3000,
        'error'
      );
    } finally {
      setExportandoTodos(false);
    }
  };

  const handleExport = async () => {
    const annotateF = (folio: string) => {
      if (folioAnteriorSet.has(folio)) return `${folio} (Cancelado)`;
      if (folioNuevoSet.has(folio)) return `${folio} (Vigente)`;
      return folio;
    };

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Reporte');
    sheet.properties.defaultRowHeight = 20;

    sheet.columns = [
      { key: 'a', width: 22 },
      { key: 'b', width: 18 },
      { key: 'c', width: 10 },
      { key: 'd', width: 45 },
      { key: 'e', width: 15 },
      { key: 'f', width: 10 },
    ];

    // Título
    sheet.mergeCells('A1:F1');
    sheet.getCell('A1').value = 'Reporte de Cargas y Abonos';
    sheet.getRow(1).height = 35;
    sheet.getRow(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: 'FFFF8C24' } };

    // Información del cliente
    let rowIdx = 2;
    for (let i = 0; i < clienteInfoList.length; i++) {
      const ci = clienteInfoList[i];
      if (i > 0) rowIdx++;

      if (ci.nombre) {
        sheet.getCell(`A${rowIdx}`).value = 'Cliente:';
        sheet.getCell(`A${rowIdx}`).font = { bold: true };
        sheet.getCell(`B${rowIdx}`).value = ci.nombre;
        rowIdx++;
      }
      if (ci.telefono) {
        sheet.getCell(`A${rowIdx}`).value = 'Teléfono:';
        sheet.getCell(`A${rowIdx}`).font = { bold: true };
        sheet.getCell(`B${rowIdx}`).value = maskPhoneNumber(ci.telefono);
        rowIdx++;
      }
      if (ci.ticketsActivos.length > 0) {
        sheet.getCell(`A${rowIdx}`).value = 'Folios activos:';
        sheet.getCell(`A${rowIdx}`).font = { bold: true };
        sheet.getCell(`B${rowIdx}`).value = ci.ticketsActivos
          .map((t) => (t.saldo ? `${t.folio} (Saldo: ${t.saldo})` : t.folio))
          .join(', ');
        rowIdx++;
      }
      if (ci.ticketsCancelados.length > 0) {
        sheet.getCell(`A${rowIdx}`).value = 'Traspasos:';
        sheet.getCell(`A${rowIdx}`).font = { bold: true };
        sheet.getCell(`B${rowIdx}`).value = ci.ticketsCancelados
          .map((t) => `${t.folio_anterior} → ${t.folio_nuevo}`)
          .join(', ');
        rowIdx++;
      }
    }

    rowIdx++; // separador en blanco

    // Encabezados de tabla
    sheet.getCell(`A${rowIdx}`).value = 'Folio';
    sheet.getCell(`B${rowIdx}`).value = 'Día Administrativo';
    sheet.getCell(`C${rowIdx}`).value = 'Tipo';
    sheet.getCell(`D${rowIdx}`).value = 'Descripción';
    sheet.getCell(`E${rowIdx}`).value = 'Monto';
    sheet.getCell(`F${rowIdx}`).value = 'Cantidad';
    sheet.getRow(rowIdx).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };
    sheet.getRow(rowIdx).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
    rowIdx++;

    // Filas de datos
    for (const r of filteredRows) {
      sheet.getCell(`A${rowIdx}`).value = annotateF(r.folio);
      sheet.getCell(`B${rowIdx}`).value = r.dia_label;
      sheet.getCell(`C${rowIdx}`).value = r.tipo;
      sheet.getCell(`D${rowIdx}`).value = r.descripcion;
      sheet.getCell(`E${rowIdx}`).value = r.tipo === 'CARGA' ? r.monto : -r.monto;
      sheet.getCell(`F${rowIdx}`).value = r.cantidad ?? '';
      rowIdx++;
    }

    // Totales
    if (mostrarTotales) {
      const totals: [string, string, string, string, number, string][] = [
        ['', '', 'CARGA', 'TOTAL CARGAS', totalCargas, ''],
        ['', '', 'ABONO', 'TOTAL ABONOS', -totalAbonos, ''],
        ['', '', '', 'SALDO RESULTANTE', totalCargas - totalAbonos, ''],
      ];
      for (const [a, b, c, d, e, f] of totals) {
        sheet.getCell(`A${rowIdx}`).value = a;
        sheet.getCell(`B${rowIdx}`).value = b;
        sheet.getCell(`C${rowIdx}`).value = c;
        sheet.getCell(`D${rowIdx}`).value = d;
        sheet.getCell(`E${rowIdx}`).value = e;
        sheet.getCell(`F${rowIdx}`).value = f;
        sheet.getRow(rowIdx).font = { bold: true, color: { argb: 'FFFFFFFF' } };
        sheet.getRow(rowIdx).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A1A' } };
        rowIdx++;
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `reporte_cargas_abonos_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(anchor.href);
  };

  const folioAnteriorSet = new Set<string>();
  const folioNuevoSet = new Set<string>();
  clienteInfoList.forEach((ci) => {
    ci.ticketsCancelados.forEach((tc) => {
      folioAnteriorSet.add(tc.folio_anterior);
      folioNuevoSet.add(tc.folio_nuevo);
    });
  });

  const columns: any = [
    {
      id: 'folio',
      name: <div className="fw-bolder text-muted">Folio</div>,
      selector: (row: ReporteCARow) => row.folio,
      cell: (row: ReporteCARow) => (
        <div>
          <div className="fw-semibold">{row.folio}</div>
          {folioAnteriorSet.has(row.folio) && (
            <div className="text-danger fs-9 fw-bold">Cancelado</div>
          )}
          {folioNuevoSet.has(row.folio) && (
            <div className="text-success fs-9 fw-bold">Vigente</div>
          )}
        </div>
      ),
      sortable: true,
      width: '110px',
    },
    {
      id: 'dia_label',
      name: <div className="fw-bolder text-muted">Día Administrativo</div>,
      selector: (row: ReporteCARow) => row.dia_label,
      sortable: true,
      width: '155px',
    },
    {
      id: 'tipo',
      name: <div className="fw-bolder text-muted">Tipo</div>,
      selector: (row: ReporteCARow) => row.tipo,
      cell: (row: ReporteCARow) => (
        <span
          className={`badge ${
            row.tipo === 'CARGA' ? 'badge-light-success' : 'badge-light-danger'
          }`}
        >
          {row.tipo}
        </span>
      ),
      sortable: true,
      width: '90px',
    },
    {
      id: 'descripcion',
      name: <div className="fw-bolder text-muted">Descripción</div>,
      selector: (row: ReporteCARow) => row.descripcion,
      sortable: true,
      wrap: true,
      grow: 2,
    },
    {
      id: 'monto',
      name: <div className="fw-bolder text-muted">Monto</div>,
      selector: (row: ReporteCARow) => row.monto,
      cell: (row: ReporteCARow) => (
        <span
          className={`fw-bold ${
            row.tipo === 'CARGA' ? 'text-success' : 'text-danger'
          }`}
        >
          {row.tipo === 'CARGA' ? '+' : '-'}
          {formatPrice(row.monto, 'es-MX', 'MXN', 2, 2)}
        </span>
      ),
      sortable: true,
      width: '130px',
    },
    {
      id: 'cantidad',
      name: <div className="fw-bolder text-muted">Cantidad</div>,
      selector: (row: ReporteCARow) => row.cantidad ?? '—',
      sortable: false,
      width: '90px',
    },
  ];

  const hasResults = rows.length > 0;

  return (
    <>
      {/* Búsqueda */}
      <div className="card mb-6">
        <div className="card-header border-0">
          <h3 className="card-title">
            <span className="card-label fw-bold text-gray-800">
              Reporte de Cargas y Abonos
            </span>
          </h3>
          <div className="card-toolbar">
            <div className="d-flex flex-column gap-2 border rounded bg-light px-4 py-2">
              {/* Fila 1: toggle + rangos */}
              <div className="d-flex align-items-center flex-wrap gap-3">
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="checkTodosFolios"
                    checked={todosFoliosEnabled}
                    onChange={(e) => handleTodosFoliosToggle(e.target.checked)}
                    disabled={loadingListado}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="checkTodosFolios">
                    {loadingListado ? 'Cargando...' : 'Todos los Folios'}
                  </label>
                </div>

                {todosFoliosEnabled && !loadingListado && listadoFolios.length > 0 && (
                  <>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="checkIncluirWallet"
                        checked={incluirWallet}
                        onChange={(e) => {
                          setIncluirWallet(e.target.checked);
                          setFolioInicio(null);
                          setFolioFin(null);
                        }}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="checkIncluirWallet">
                        Incluir Wallet
                      </label>
                    </div>
                    <div style={{ minWidth: '140px' }}>
                      <Select
                        placeholder="Folio inicial"
                        options={folioOptions}
                        value={folioInicio ? { value: folioInicio, label: folioInicio } : null}
                        onChange={(opt: any) => {
                          setFolioInicio(opt?.value ?? null);
                          setFolioFin(null);
                        }}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{ ...styleSelect, menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                        theme={themeSelect}
                        classNamePrefix="tc-select"
                      />
                    </div>
                    <span className="fw-bold text-muted">—</span>
                    <div style={{ minWidth: '140px' }}>
                      <Select
                        placeholder="Folio final"
                        options={folioFinOptions}
                        value={folioFin ? { value: folioFin, label: folioFin } : null}
                        onChange={(opt: any) => setFolioFin(opt?.value ?? null)}
                        isDisabled={!folioInicio}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{ ...styleSelect, menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                        theme={themeSelect}
                        classNamePrefix="tc-select"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Fila 2: conteo + check totales */}
              {todosFoliosEnabled && !loadingListado && listadoFolios.length > 0 && (
                <div className="d-flex align-items-center flex-wrap gap-3">
                  {foliosEnRango.length > 0 && (
                    <span className="text-muted fw-semibold fs-8 text-nowrap">
                      <span className="badge badge-light-primary me-1">{foliosEnRango.length} folios</span>
                      · tardará unos segundos
                    </span>
                  )}
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="checkTotalesExport"
                      checked={mostrarTotalesExport}
                      onChange={(e) => setMostrarTotalesExport(e.target.checked)}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="checkTotalesExport">
                      Mostrar totales
                    </label>
                  </div>
                </div>
              )}

              {/* Fila 3: botón exportar */}
              {todosFoliosEnabled && !loadingListado && listadoFolios.length > 0 && (
                <div>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleExportTodos}
                    disabled={foliosEnRango.length === 0 || exportandoTodos}
                  >
                    {exportandoTodos ? (
                      <>
                        Generando...
                        <span className="spinner-border spinner-border-sm align-middle ms-2" />
                      </>
                    ) : (
                      'Exportar Excel'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card-body pt-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            {/* Toggle tipo búsqueda */}
            <div className="btn-group">
              {(['folio', 'telefono', 'nombre'] as SearchTypeCA[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleToggle(t)}
                  className={`btn btn-sm ${
                    searchType === t ? 'btn-primary' : 'btn-light-primary'
                  }`}
                >
                  {SEARCH_LABELS[t]}
                </button>
              ))}
            </div>

            <input
              type="text"
              placeholder={SEARCH_PLACEHOLDERS[searchType]}
              value={searchValue}
              onChange={
                searchType === 'telefono'
                  ? handlePhoneChange
                  : (e) => setSearchValue(e.target.value)
              }
              className="form-control w-250px"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleBuscar();
              }}
            />

            {searchType === 'nombre' && (
              <span className="badge badge-warning fs-8 fw-bold px-3 py-2 text-dark">
                📱 Solo clientes Wallet móvil
              </span>
            )}

            <button
              type="button"
              onClick={handleBuscar}
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  Buscando...
                  <span className="spinner-border spinner-border-sm align-middle ms-2" />
                </>
              ) : (
                'Buscar'
              )}
            </button>
          </div>

          {/* Filtro días administrativos */}
          {hasResults && (
            <div className="d-flex align-items-center mt-5">
              <label className="form-label fw-bold mb-0 me-3 text-nowrap">
                Días Administrativos:
              </label>
              <div style={{ minWidth: '320px' }}>
                <Select
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  placeholder="Todos los días"
                  options={diasList}
                  value={diasSeleccionados}
                  onChange={(opts) =>
                    setDiasSeleccionados((opts as any[]) ?? [])
                  }
                  styles={styleSelect}
                  theme={themeSelect}
                  classNamePrefix="tc-select"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Información del cliente */}
      {clienteInfoList.some(
        (ci) =>
          ci.nombre ||
          ci.telefono ||
          ci.ticketsActivos.length > 0 ||
          ci.ticketsCancelados.length > 0
      ) && (
        <div className="card mb-6">
          <div className="card-header border-0">
            <h3 className="card-title">
              <span className="card-label fw-bold text-gray-800">
                Información del cliente
              </span>
            </h3>
          </div>
          <div className="card-body pt-2">
            {clienteInfoList.map((ci, idx) => (
              <div
                key={idx}
                className={idx > 0 ? 'mt-5 pt-5 border-top' : ''}
              >
                <div className="d-flex flex-wrap gap-8 mb-3">
                  {ci.nombre && (
                    <div>
                      <span className="fw-bold text-gray-700">Nombre: </span>
                      <span className="text-gray-600">{ci.nombre}</span>
                    </div>
                  )}
                  {ci.telefono && (
                    <div>
                      <span className="fw-bold text-gray-700">Teléfono: </span>
                      <span className="text-gray-600">
                        {maskPhoneNumber(ci.telefono)}
                      </span>
                    </div>
                  )}
                </div>

                {ci.ticketsActivos.length > 0 && (
                  <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                    <span className="fw-bold text-muted me-1">Activos:</span>
                    {ci.ticketsActivos.map((ta, i) => (
                      <span key={i} className="badge badge-light-success fs-7">
                        {ta.folio}
                        {ta.saldo
                          ? ` — ${formatPrice(Number(ta.saldo), 'es-MX', 'MXN', 2, 2)}`
                          : ''}
                      </span>
                    ))}
                  </div>
                )}

                {ci.ticketsCancelados.length > 0 && (
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <span className="fw-bold text-muted me-1">Cancelados:</span>
                    {ci.ticketsCancelados.map((tc, i) => (
                      <span key={i} className="badge badge-light-danger fs-7">
                        {tc.folio_anterior} → {tc.folio_nuevo}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabla principal */}
      {hasResults && (
        <div className="card mb-6">
          <div className="card-header border-0 pt-5">
            <div className="card-title">
              <span className="card-label fw-bold text-gray-800">
                Movimientos
                <span className="text-muted fw-normal fs-7 ms-2">
                  ({filteredRows.length} registros)
                </span>
              </span>
            </div>
            <div className="card-toolbar d-flex align-items-center gap-5">
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="checkTotales"
                  checked={mostrarTotales}
                  onChange={(e) => setMostrarTotales(e.target.checked)}
                />
                <label className="form-check-label fw-semibold" htmlFor="checkTotales">
                  Mostrar totales
                </label>
              </div>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={handleExport}
                disabled={filteredRows.length === 0}
              >
                Exportar Excel
              </button>
            </div>
          </div>

          <div className="card-body">
            <TableList data={filteredRows} columns={columns} />

            {mostrarTotales && (
              <div className="d-flex justify-content-end mt-6">
                <div className="border rounded px-8 py-4 bg-light d-flex gap-10">
                  <div className="text-center">
                    <div className="text-muted fw-bold fs-8 text-uppercase mb-1">
                      Total Cargas
                    </div>
                    <div className="fw-bolder fs-5 text-success">
                      +{formatPrice(totalCargas, 'es-MX', 'MXN', 2, 2)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted fw-bold fs-8 text-uppercase mb-1">
                      Total Abonos
                    </div>
                    <div className="fw-bolder fs-5 text-danger">
                      -{formatPrice(totalAbonos, 'es-MX', 'MXN', 2, 2)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted fw-bold fs-8 text-uppercase mb-1">
                      Saldo resultante
                    </div>
                    <div
                      className={`fw-bolder fs-5 ${
                        totalCargas - totalAbonos >= 0
                          ? 'text-primary'
                          : 'text-danger'
                      }`}
                    >
                      {formatPrice(totalCargas - totalAbonos, 'es-MX', 'MXN', 2, 2)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export { View };
